import { useState } from 'react'
import { useAssociados } from '@/hooks/useAssociados'
import { useVeiculos, useCreateVeiculo } from '@/hooks/useVeiculos'
import { useAllServicos } from '@/hooks/useServicos'
import { useCreateContrato } from '@/hooks/useContratos'
import { contratoService } from '@/services/contrato.service'
import { mascararCPF } from '@/lib/validators'
import { formatCurrency } from '@/lib/formatters'
import type { Associado, Servico } from '@/types/database'
import {
    X, ChevronRight, ChevronLeft, Search, User, Car, Package,
    CheckCircle2, Loader2, Plus, ArrowRight,
} from 'lucide-react'

/* ── Tipos ─────────────────────────────────────────────── */

interface WizardState {
    step: 1 | 2 | 3 | 4
    associado: Associado | null
    veiculo: { id: string; placa: string; marca: string; modelo: string } | null
    servicosSelecionados: Servico[]
    dia_vencimento: number
    ciclo: 'mensal' | 'trimestral' | 'semestral' | 'anual'
}

const INITIAL: WizardState = {
    step: 1, associado: null, veiculo: null, servicosSelecionados: [],
    dia_vencimento: 10, ciclo: 'mensal',
}

/* ── Componente Principal ──────────────────────────────── */

export function NovoContratoWizard({ onClose, initialAssociado }: { onClose: () => void; initialAssociado?: Associado | null }) {
    const [state, setState] = useState<WizardState>({
        ...INITIAL,
        associado: initialAssociado || null,
        step: initialAssociado ? 2 : 1,
    })
    const createContrato = useCreateContrato()

    const setStep = (step: WizardState['step']) => setState(prev => ({ ...prev, step }))

    const valorTotal = state.servicosSelecionados.reduce((sum, s) => sum + s.valor_mensal, 0)

    const handleCreate = async () => {
        if (!state.associado || state.servicosSelecionados.length === 0) return

        try {
            await contratoService.checkoutPortal({
                associado_id: state.associado.id,
                veiculo_id: state.veiculo?.id || null,
                placa: state.veiculo?.placa || null,
                tenant_id: state.associado.tenant_id,
                servico_id: state.servicosSelecionados[0]?.id || '',
                // @ts-ignore
                servico_ids: state.servicosSelecionados.map(s => s.id),
                dia_vencimento: state.dia_vencimento as any,
                valor_mensal: valorTotal,
                valor_adesao: 0, // Admin-created contracts usually don't trigger auto-adesao checkout unless specified
                adesao_pro_rata: false,
                termo_aceite_em: new Date().toISOString(),
                assinatura_digital: {
                    hash: `admin_manual_${Date.now()}`,
                    timestamp: new Date().toISOString(),
                    user_agent: 'Backoffice-Admin',
                    documento_base: 'Contratação via Backoffice (Aprovado por Admin)'
                }
            })
            onClose()
        } catch (err: any) {
            console.error('Erro ao criar contrato via wizard:', err)
            alert('Erro ao criar contrato: ' + err.message)
        }
    }

    const steps = [
        { num: 1, label: 'Associado', icon: User },
        { num: 2, label: 'Serviços', icon: Package },
        { num: 3, label: 'Veículo', icon: Car },
        { num: 4, label: 'Confirmar', icon: CheckCircle2 },
    ] as const

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden rounded-3xl shadow-elevated">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-surface-100">
                    <div>
                        <h3 className="text-lg font-semibold text-surface-900">Novo Contrato</h3>
                        <p className="text-xs text-surface-700/60 mt-0.5">Passo {state.step} de 4</p>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-100 transition-colors"><X size={18} /></button>
                </div>

                {/* Stepper */}
                <div className="flex items-center gap-1 px-5 py-3 border-b border-surface-50">
                    {steps.map((s, idx) => {
                        const Icon = s.icon
                        const done = state.step > s.num
                        const active = state.step === s.num
                        return (
                            <div key={s.num} className="flex items-center gap-1 flex-1">
                                <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all
                                    ${active ? 'bg-primary-600 text-white shadow-sm' :
                                        done ? 'bg-success-100 text-success-700' :
                                            'text-surface-700/40'}`}>
                                    <Icon size={14} />
                                    <span className="hidden sm:inline">{s.label}</span>
                                </div>
                                {idx < 3 && <ArrowRight size={12} className="text-surface-700/20 flex-shrink-0" />}
                            </div>
                        )
                    })}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5">
                    {state.step === 1 && (
                        <StepAssociado
                            selected={state.associado}
                            onSelect={(a) => setState(prev => ({ ...prev, associado: a, veiculo: null }))}
                        />
                    )}
                    {state.step === 2 && (
                        <StepServicos
                            selecionados={state.servicosSelecionados}
                            onToggle={(s) => setState(prev => {
                                const exists = prev.servicosSelecionados.find(x => x.id === s.id)
                                return {
                                    ...prev,
                                    servicosSelecionados: exists
                                        ? prev.servicosSelecionados.filter(x => x.id !== s.id)
                                        : [...prev.servicosSelecionados, s],
                                }
                            })}
                        />
                    )}
                    {state.step === 3 && state.associado && (
                        <StepVeiculo
                            associadoId={state.associado.id}
                            associadoNome={state.associado.nome}
                            selected={state.veiculo}
                            onSelect={(v) => setState(prev => ({ ...prev, veiculo: v }))}
                            precisaVeiculo={state.servicosSelecionados.some(s => s.exige_veiculo)}
                        />
                    )}
                    {state.step === 4 && (
                        <StepRevisao
                            state={state}
                            onChange={(key, val) => setState(prev => ({ ...prev, [key]: val }))}
                            valorTotal={valorTotal}
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between p-5 border-t border-surface-100">
                    <div>
                        {state.step > 1 && (
                            <button onClick={() => setStep((state.step - 1) as WizardState['step'])}
                                className="btn btn-secondary btn-md gap-1">
                                <ChevronLeft size={16} /> Voltar
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        {valorTotal > 0 && state.step >= 3 && (
                            <p className="text-sm text-surface-700/60">Total: <span className="font-bold text-surface-900">{formatCurrency(valorTotal)}</span>/mês</p>
                        )}
                        {state.step < 4 ? (
                            <button
                                onClick={() => {
                                    const precisaVeiculo = state.servicosSelecionados.some(s => s.exige_veiculo)
                                    if (state.step === 2 && !precisaVeiculo) {
                                        setState(prev => ({ ...prev, veiculo: null, step: 4 }))
                                    } else {
                                        setStep((state.step + 1) as WizardState['step'])
                                    }
                                }}
                                disabled={
                                    (state.step === 1 && !state.associado) ||
                                    (state.step === 2 && state.servicosSelecionados.length === 0) ||
                                    (state.step === 3 && state.servicosSelecionados.some(s => s.exige_veiculo) && !state.veiculo)
                                }
                                className="btn btn-primary btn-md gap-1"
                            >
                                Próximo <ChevronRight size={16} />
                            </button>
                        ) : (
                            <button onClick={handleCreate} disabled={createContrato.isPending}
                                className="btn btn-primary btn-md gap-2">
                                {createContrato.isPending ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                                Criar Contrato
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

/* ── Step 1: Selecionar Associado ──────────────────────── */

function StepAssociado({ selected, onSelect }: {
    selected: Associado | null
    onSelect: (a: Associado) => void
}) {
    const [search, setSearch] = useState('')
    const { data: result, isLoading } = useAssociados({ search, pageSize: 50 })
    const associados = (result?.data || []).filter(a => a.status === 'ativo')

    return (
        <div className="space-y-4 animate-fade-in">
            <div>
                <h4 className="font-semibold text-surface-900">Selecionar Associado</h4>
                <p className="text-sm text-surface-700/60">Escolha o associado que receberá o novo contrato</p>
            </div>

            <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-700/40" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Buscar por nome ou CPF..." className="input pl-9 text-sm" autoFocus />
            </div>

            {isLoading ? (
                <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary-600" /></div>
            ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                    {associados.map(a => (
                        <button key={a.id} onClick={() => onSelect(a)}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left
                                ${selected?.id === a.id
                                    ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-400'
                                    : 'border-surface-100 hover:border-primary-200 hover:bg-surface-50'}`}>
                            <div className="w-9 h-9 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                                <User size={16} className="text-primary-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-surface-900 truncate">{a.nome}</p>
                                <p className="text-xs text-surface-700/60">{mascararCPF(a.cpf)} • {a.endereco?.cidade}/{a.endereco?.uf}</p>
                            </div>
                            {selected?.id === a.id && <CheckCircle2 size={18} className="text-primary-600 flex-shrink-0" />}
                        </button>
                    ))}
                    {associados.length === 0 && (
                        <p className="text-center py-6 text-surface-700/50 text-sm">Nenhum associado ativo encontrado</p>
                    )}
                </div>
            )}
        </div>
    )
}

/* ── Step 2: Selecionar Veículo ────────────────────────── */

function StepVeiculo({ associadoId, associadoNome, selected, onSelect, precisaVeiculo }: {
    associadoId: string
    associadoNome: string
    selected: WizardState['veiculo']
    onSelect: (v: WizardState['veiculo']) => void
    precisaVeiculo: boolean
}) {
    const { data: result, isLoading } = useVeiculos({ associado_id: associadoId, pageSize: 50 })
    const veiculos = result?.data || []
    const [showAddVeiculo, setShowAddVeiculo] = useState(false)
    const createVeiculo = useCreateVeiculo()
    const [newVeiculo, setNewVeiculo] = useState({ placa: '', marca: '', modelo: '' })

    const handleAddVeiculo = async () => {
        if (!newVeiculo.placa || !newVeiculo.marca || !newVeiculo.modelo) return
        const created = await createVeiculo.mutateAsync({
            placa: newVeiculo.placa.toUpperCase(),
            marca: newVeiculo.marca,
            modelo: newVeiculo.modelo,
            associado_id: associadoId,
        })
        onSelect({ id: created.id, placa: created.placa, marca: created.marca, modelo: created.modelo })
        setShowAddVeiculo(false)
    }

    return (
        <div className="space-y-4 animate-fade-in">
            <div>
                <h4 className="font-semibold text-surface-900">Selecionar Veículo <span className="text-xs font-normal text-surface-700/60">(opcional)</span></h4>
                <p className="text-sm text-surface-700/60">Veículos de <strong>{associadoNome}</strong>. Pode pular se o serviço não envolve veículo.</p>
            </div>

            {/* Skip vehicle button — only if not mandatory */}
            {!precisaVeiculo && (
                <button onClick={() => onSelect(null)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 border-dashed transition-all text-left
                        ${selected === null
                            ? 'border-primary-400 bg-primary-50/50'
                            : 'border-surface-200 hover:border-primary-200 hover:bg-surface-50'}`}>
                    <div className="w-9 h-9 rounded-xl bg-surface-100 flex items-center justify-center flex-shrink-0">
                        <User size={16} className="text-surface-700/60" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-surface-900">Sem veículo — somente CPF</p>
                        <p className="text-xs text-surface-700/60">Contrato vinculado apenas ao associado</p>
                    </div>
                    {selected === null && <CheckCircle2 size={18} className="text-primary-600 flex-shrink-0" />}
                </button>
            )}

            {precisaVeiculo ? (
                <div>
                    <h4 className="font-semibold text-danger-600 text-sm mb-4">⚠️ Este contrato exige a seleção de um veículo.</h4>
                </div>
            ) : (
                <div className="relative flex items-center">
                    <div className="flex-1 border-t border-surface-200" />
                    <span className="px-3 text-xs text-surface-700/40 uppercase">ou selecione um veículo</span>
                    <div className="flex-1 border-t border-surface-200" />
                </div>
            )}

            {isLoading ? (
                <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary-600" /></div>
            ) : (
                <div className="space-y-2">
                    {veiculos.map(v => (
                        <button key={v.id} onClick={() => onSelect({ id: v.id, placa: v.placa, marca: v.marca, modelo: v.modelo })}
                            className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left
                                ${selected?.id === v.id
                                    ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-400'
                                    : 'border-surface-100 hover:border-primary-200 hover:bg-surface-50'}`}>
                            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <Car size={16} className="text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-mono font-semibold text-sm text-surface-900">{v.placa}</p>
                                <p className="text-xs text-surface-700/60">{v.marca} {v.modelo} {v.ano ? `• ${v.ano}` : ''}</p>
                            </div>
                            {selected?.id === v.id && <CheckCircle2 size={18} className="text-primary-600 flex-shrink-0" />}
                        </button>
                    ))}
                    {veiculos.length === 0 && !showAddVeiculo && (
                        <div className="text-center py-6">
                            <p className="text-surface-700/50 text-sm mb-3">Nenhum veículo cadastrado</p>
                        </div>
                    )}
                </div>
            )}

            {/* Add Vehicle Inline */}
            {!showAddVeiculo ? (
                <button onClick={() => setShowAddVeiculo(true)}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-surface-200 text-surface-700/60 hover:border-primary-300 hover:text-primary-600 transition-colors text-sm">
                    <Plus size={16} /> Cadastrar novo veículo
                </button>
            ) : (
                <div className="p-4 rounded-xl border border-primary-200 bg-primary-50/30 space-y-3">
                    <p className="text-sm font-medium text-surface-900">Novo Veículo</p>
                    <div className="grid grid-cols-3 gap-2">
                        <input className="input text-sm" placeholder="Placa" value={newVeiculo.placa}
                            onChange={e => setNewVeiculo(p => ({ ...p, placa: e.target.value }))} />
                        <input className="input text-sm" placeholder="Marca" value={newVeiculo.marca}
                            onChange={e => setNewVeiculo(p => ({ ...p, marca: e.target.value }))} />
                        <input className="input text-sm" placeholder="Modelo" value={newVeiculo.modelo}
                            onChange={e => setNewVeiculo(p => ({ ...p, modelo: e.target.value }))} />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setShowAddVeiculo(false)} className="btn btn-secondary text-xs px-3 py-1.5">Cancelar</button>
                        <button onClick={handleAddVeiculo} disabled={createVeiculo.isPending}
                            className="btn btn-primary text-xs px-3 py-1.5 gap-1">
                            {createVeiculo.isPending && <Loader2 size={14} className="animate-spin" />}
                            Cadastrar
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

/* ── Step 3: Selecionar Serviços ───────────────────────── */

function StepServicos({ selecionados, onToggle }: {
    selecionados: Servico[]
    onToggle: (s: Servico) => void
}) {
    const { data: servicos = [], isLoading } = useAllServicos()
    const total = selecionados.reduce((sum, s) => sum + s.valor_mensal, 0)

    return (
        <div className="space-y-4 animate-fade-in">
            <div>
                <h4 className="font-semibold text-surface-900">Selecionar Serviços</h4>
                <p className="text-sm text-surface-700/60">Escolha os serviços que farão parte deste contrato</p>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-primary-600" /></div>
            ) : (
                <div className="space-y-2">
                    {servicos.map(s => {
                        const isChecked = selecionados.some(x => x.id === s.id)
                        return (
                            <button key={s.id} onClick={() => onToggle(s)}
                                className={`w-full flex items-start gap-3 p-4 rounded-xl border transition-all text-left
                                    ${isChecked
                                        ? 'border-primary-400 bg-primary-50 ring-1 ring-primary-400'
                                        : 'border-surface-100 hover:border-primary-200 hover:bg-surface-50'}`}>
                                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors
                                    ${isChecked ? 'bg-primary-600 border-primary-600' : 'border-surface-300'}`}>
                                    {isChecked && <CheckCircle2 size={14} className="text-white" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                        <p className="font-medium text-sm text-surface-900">{s.nome}</p>
                                        <p className="font-bold text-primary-600 text-sm">{formatCurrency(s.valor_mensal)}<span className="text-xs font-normal text-surface-700/60">/mês</span></p>
                                    </div>
                                    <p className="text-xs text-surface-700/60 mt-0.5 line-clamp-2">{s.descricao}</p>
                                    {s.carencia_dias > 0 && (
                                        <p className="text-xs text-warning-600 mt-1">⏳ Carência: {s.carencia_dias} dias</p>
                                    )}
                                </div>
                            </button>
                        )
                    })}
                </div>
            )}

            {selecionados.length > 0 && (
                <div className="p-4 rounded-xl border border-surface-100">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-surface-700">{selecionados.length} serviço{selecionados.length > 1 ? 's' : ''} selecionado{selecionados.length > 1 ? 's' : ''}</p>
                        <p className="text-lg font-bold text-surface-900">{formatCurrency(total)}<span className="text-sm font-normal text-surface-700/60">/mês</span></p>
                    </div>
                </div>
            )}
        </div>
    )
}

/* ── Step 4: Revisão ───────────────────────────────────── */

function StepRevisao({ state, onChange, valorTotal }: {
    state: WizardState
    onChange: (key: string, val: unknown) => void
    valorTotal: number
}) {
    return (
        <div className="space-y-5 animate-fade-in">
            <div>
                <h4 className="font-semibold text-surface-900">Revisão do Contrato</h4>
                <p className="text-sm text-surface-700/60">Confira os dados antes de criar o contrato</p>
            </div>

            {/* Associado + Veículo */}
            <div className={`grid gap-3 ${state.veiculo ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div className="p-3 rounded-xl border border-surface-100">
                    <p className="text-xs text-surface-700/60 uppercase font-medium mb-1">Associado</p>
                    <p className="text-sm font-semibold text-surface-900">{state.associado?.nome}</p>
                    <p className="text-xs text-surface-700/60">{mascararCPF(state.associado?.cpf || '')}</p>
                </div>
                {state.veiculo ? (
                    <div className="p-3 rounded-xl border border-surface-100">
                        <p className="text-xs text-surface-700/60 uppercase font-medium mb-1">Veículo</p>
                        <p className="text-sm font-semibold font-mono text-surface-900">{state.veiculo.placa}</p>
                        <p className="text-xs text-surface-700/60">{state.veiculo.marca} {state.veiculo.modelo}</p>
                    </div>
                ) : (
                    <div className="p-3 rounded-xl border border-primary-100 bg-primary-50/30">
                        <p className="text-xs text-primary-600 uppercase font-medium mb-1">Tipo</p>
                        <p className="text-sm font-semibold text-primary-700">Contrato somente CPF</p>
                        <p className="text-xs text-primary-600">Sem veículo vinculado</p>
                    </div>
                )}
            </div>

            {/* Serviços */}
            <div className="rounded-xl border border-surface-100 overflow-hidden">
                <div className="px-4 py-2.5 bg-white border-b border-surface-100">
                    <p className="text-xs text-surface-700/60 uppercase font-medium">Serviços contratados</p>
                </div>
                {state.servicosSelecionados.map(s => (
                    <div key={s.id} className="flex items-center justify-between px-4 py-3 border-b border-surface-50 last:border-0">
                        <div>
                            <p className="text-sm font-medium text-surface-900">{s.nome}</p>
                            {s.carencia_dias > 0 && <p className="text-xs text-warning-600">Carência: {s.carencia_dias} dias</p>}
                        </div>
                        <p className="text-sm font-semibold text-surface-900">{formatCurrency(s.valor_mensal)}</p>
                    </div>
                ))}
                <div className="flex items-center justify-between px-4 py-3 bg-primary-50">
                    <p className="font-medium text-surface-900">Total mensal</p>
                    <p className="text-lg font-bold text-primary-700">{formatCurrency(valorTotal)}</p>
                </div>
            </div>

            {/* Params */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-sm font-medium text-surface-700 mb-1 block">Dia de vencimento</label>
                    <input className="input" type="number" min={1} max={28}
                        value={state.dia_vencimento}
                        onChange={e => onChange('dia_vencimento', +e.target.value)} />
                </div>
                <div>
                    <label className="text-sm font-medium text-surface-700 mb-1 block">Ciclo de faturamento</label>
                    <select className="input" value={state.ciclo}
                        onChange={e => onChange('ciclo', e.target.value)}>
                        <option value="mensal">Mensal</option>
                        <option value="trimestral">Trimestral</option>
                        <option value="semestral">Semestral</option>
                        <option value="anual">Anual</option>
                    </select>
                </div>
            </div>
        </div>
    )
}
